import * as React from 'react';
import { Alert, Col, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { EventExtendedView, InstitutionParent, InstitutionView } from 'cb-model';
import {
  useCacheRecord,
  useCacheQuery,
  useQueryOptionsFromSearchParams,
  ParallelLoad,
  PaginatorAndListSettings,
} from 'gossamer-front-end';
import { EventInstitutionCard } from '../website-events/EventInstitutionCard';
import {
  emptyWebsiteEventFilterValues,
  makeWebsiteEventQueryFilters,
  removeEventFilters,
  WebsiteEventFilterSidebar,
  WebsiteEventFilterValues,
} from '../website-events/WebsiteEventFilters';
import { useIsMobile } from '../event/hooks/useIsMobile';
import styles from './WebsiteInstitutionPage.module.css';
import { CalendarCombined } from './CalendarCombined';
import { EventTileFromRow } from 'shared-front-end';
import { QueryOptions } from 'gossamer-unified';

export const WebsiteInstitutionPage = (): React.JSX.Element => {
  const params = useParams();
  const isMobile = useIsMobile(992); // bootstrap lg breakpoint
  const [searchParams, setSearchParams] = useSearchParams();
  const [eventFilters, setEventFilters] = React.useState<WebsiteEventFilterValues>(emptyWebsiteEventFilterValues);
  const now = React.useRef(new Date().toISOString());
  const institutionId = Number(params.id);
  const institution = useCacheRecord(InstitutionView, Number.isFinite(institutionId) ? institutionId : null);
  const [queryOptions, setQueryOptions] = useQueryOptionsFromSearchParams(EventExtendedView, {
    where: {
      and: [
        { fieldId: 'institution_id', operator: 'eq', value: String(institutionId), display: 'hidden' },
        { fieldId: 'end_at', operator: 'gt', value: now.current, display: 'hidden' },
      ],
    },
    order: [['start_at', 'ASC']],
    limit: 24,
  });
  const filteredQueryOptions = React.useMemo<QueryOptions>(
    () => ({
      ...queryOptions,
      where: {
        and: [
          ...removeEventFilters(queryOptions.where?.and),
          ...makeWebsiteEventQueryFilters(eventFilters, { includeFutureDefault: false }),
        ],
      },
    }),
    [eventFilters, queryOptions],
  );
  const gCacheQueryItem = useCacheQuery(EventExtendedView, filteredQueryOptions);
  const children = useCacheQuery(InstitutionParent, {
    where: { and: [{ fieldId: 'parent_institution', operator: 'eq', value: institutionId }] },
  });

  const updateEventFilters = (nextFilters: WebsiteEventFilterValues) => {
    setEventFilters(nextFilters);
    setQueryOptions({
      ...queryOptions,
      offset: 0,
      where: { and: removeEventFilters(queryOptions.where?.and) },
    });
  };

  const updateEventQueryOptions = (nextQueryOptions: QueryOptions) => {
    setQueryOptions({
      ...nextQueryOptions,
      where: { and: removeEventFilters(nextQueryOptions.where?.and) },
    });
  };

  if (!Number.isFinite(institutionId)) return <Alert variant="danger">Invalid institution id.</Alert>;
  if (institution.isFetching) return <Spinner animation="border" />;
  if (institution.error || !institution.data) return <Alert variant="danger">Institution not found.</Alert>;

  const outerStyle: any = {};
  if (!isMobile) {
    outerStyle.background = `url(${institution.data?.banner || ''}) no-repeat center center / cover`;
  }

  return (
    <>
      <div style={outerStyle} className={isMobile ? '' : styles.InstitutionBannerContainer}>
        <div className={isMobile ? '' : styles.EventInstitutionCardContainer}>
          <EventInstitutionCard institutionId={institutionId} showBanner={isMobile} />
        </div>
      </div>

      <Tabs
        activeKey={searchParams.get('tab') || 'events'}
        variant="underline"
        className="mt-3"
        mountOnEnter={true}
        unmountOnExit={true}
        onSelect={(tab) => {
          if (!tab) {
            return;
          }
          const newParams = new URLSearchParams();
          newParams.set('tab', tab);
          setSearchParams(newParams);
        }}
      >
        <Tab eventKey="events" title="Events">
          <div className={styles.TabLayout}>
            <WebsiteEventFilterSidebar
              className={styles.FiltersPanel}
              values={eventFilters}
              onChange={updateEventFilters}
            />
            <div className={styles.TabMain}>
              <ParallelLoad responses={[gCacheQueryItem]}>
                <PaginatorAndListSettings
                  entity={EventExtendedView}
                  gCacheQuery={gCacheQueryItem}
                  queryOptions={filteredQueryOptions}
                  showItemCount={true}
                  render={(_) => (
                    <Row xs={1} md={2} lg={3} className="g-3">
                      {gCacheQueryItem.data?.map((item) => (
                        <Col key={item.id}>
                          <EventTileFromRow item={item} />
                        </Col>
                      ))}
                    </Row>
                  )}
                  setQueryOptions={updateEventQueryOptions}
                  showSettingsControl={false}
                />
              </ParallelLoad>
            </div>
          </div>
        </Tab>
        <Tab eventKey="calendar" title="Calendar">
          <div className={styles.TabLayout}>
            <WebsiteEventFilterSidebar
              className={styles.FiltersPanel}
              values={eventFilters}
              onChange={updateEventFilters}
            />
            <div className={styles.TabMain}>
              <CalendarCombined institutionId={institutionId} eventFilters={eventFilters} />
            </div>
          </div>
        </Tab>
        {institution.data?.number_of_children > 0 && (
          <Tab eventKey="children" title="Hospitals">
            <Row>
              {children.data?.map((child) => (
                <Col key={child.id} xs={12} md={6} lg={4} className="mt-3">
                  <EventInstitutionCard institutionId={child.id} showBanner showAsLink />
                </Col>
              ))}
            </Row>
          </Tab>
        )}
      </Tabs>
    </>
  );
};
